<template>
  <section class="main">
    <h1 class="heading">
      <template v-if="query">
        {{ $t("SHOWING_RESULTS_FOR", { query }) }}
      </template>
      <template v-else>
        {{ $t("SEARCH") }}
      </template>
    </h1>
    <div class="wrapper">
      <aside class="filters">
        <filters :type="type" :genre="genre" :price="price"></filters>
      </aside>
      <div class="apps" ref="apps">
        <p class="not-found" v-if="!isLoading && !results.length">
          {{
            $t("NO_RESULTS_IN_SECTION", {
              section:
                (genre &&
                  $t($store.getters.genres[type][genre].name.toUpperCase())) ||
                translateType(type),
            })
          }}
        </p>
        <app
          ref="app"
          v-for="app in results"
          :app="app"
          :type="type"
          :key="app.id"
        />
        <loader class="apps__loader" v-show="!isLastPage"></loader>
      </div>
      <aside class="categories">
        <h2 class="categories__heading">{{ $t("EXPLORE_CATEGORIES") }}</h2>
        <ul>
          <li class="category" v-for="genre in genres" :key="genre.id">
            <a
              class="category__link"
              href="#"
              @click.prevent="goToGenre(genre.id)"
            >
              {{ $t(genre.name.toUpperCase()) }}
              <span class="category__count">{{ genre.content_amount }}</span>
            </a>
          </li>
        </ul>
      </aside>
    </div>
  </section>
</template>

<script>
import Filters from '@/components/Filters';
import App from '@/components/App';
import Loader from '@/components/Loader';
import { throttle, isEqual } from 'lodash';

export default {
  name: 'search',
  components: { Filters, App, Loader },
  data() {
    return {
      isLoading: true,
      results: [],
      page: 1,
      isLastPage: false,
      params: { ...this.$route.query },
    };
  },
  computed: {
    title() {
      return this.$t('SEARCH');
    },
    query() {
      return this.$route.query.query;
    },
    type() {
      const { type } = this.$route.query;
      return this.translateType(type) ? type : 'ios';
    },
    dev() {
      return this.$route.query.dev;
    },
    genre() {
      const { genre } = this.$route.query;
      if (genre && !this.$store.getters.genres[this.type][genre]) {
        return null;
      }
      return genre;
    },
    price() {
      const { price } = this.$route.query;
      if (price !== '1' && price !== '2') {
        return null;
      }

      return price;
    },
    genres() {
      return Object.values(this.$store.getters.genres[this.type] || {})
        .sort((a, b) => b.content_amount - a.content_amount);
    },
  },
  methods: {
    translateType(type) {
      const dict = {
        ios: 'iOS',
        cydia: 'Cydia',
        osx: 'macOS (AppStore)',
        standalone: 'macOS (Standalone)',
        tvos: 'Apple TV',
        books: this.$t('BOOKS'),
      };

      return dict[type];
    },
    async resetScroll() {
      await this.$nextTick();
      const rect = (this.$refs.apps && this.$refs.apps.getBoundingClientRect()) || { top: -1 };
      if (rect.top < 0) {
        window.scrollTo(0, 0);
      }
    },
    goToGenre(id) {
      this.$router.push({ query: { ...this.$route.query, genre: id } });
    },
    search(page) {
      if (!page) {
        this.results = [];
        this.page = 1;
      }
      this.isLoading = true;
      const params = { type: this.type };
      if (this.query) {
        params.q = this.query;
      }
      if (this.dev) {
        params.dev = this.dev;
      }
      if (this.genre) {
        params.genre = this.genre;
      }
      if (this.price) {
        params.price = this.price;
      }
      if (page) {
        params.page = page;
      }
      return this.$api.search(params).then((data) => {
        this.isLoading = false;
        if (data.length < 25) {
          this.isLastPage = true;
        }
        this.results = [...this.results, ...data];
      });
    },
    // eslint-disable-next-line
    handleScroll: throttle(function () {
      if (this.isLoading || this.isLastPage || !this.$refs.app || this.$route.name !== 'search') {
        return;
      }

      const index = this.results.length - 20;
      const app = this.$refs.app[index >= 0 ? index : 0];
      if (!app) {
        return;
      }
      const breakpoint = app.$el.offsetTop;

      if (window.scrollY > breakpoint) {
        this.page = this.page + 1;
        this.search(this.page);
      }
    }, 1000),
  },
  watch: {
    '$route.query': {
      handler() {
        if (this.$route.name === 'search' && !isEqual(this.$route.query, this.params)) {
          this.params = { ...this.$route.query };
          this.isLastPage = false;
          this.resetScroll();
          this.search();
        }
      },
      deep: true,
    },
  },
  serverPrefetch() {
    return this.search();
  },
  created() {
    this.search();
  },
  mounted() {
    this.resetScroll();
    window.addEventListener('scroll', this.handleScroll);
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.wrapper {
  display: flex;
  align-items: flex-start;

  @include notebook {
    flex-direction: column;
    align-items: center;
  }

  @media (max-width: 767px) {
    align-items: stretch;
  }
}

.heading {
  display: block;
  width: 100%;
  color: var(--text-tertiary);
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  word-break: break-word;

  @include notebook {
    text-align: center;
  }

  @media (max-width: 767px) {
    text-align: left;
  }
}

.filters {
  width: 180px;

  @include notebook {
    width: auto;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
}

.apps {
  margin: 0 20px;
  flex-grow: 1;

  &__loader {
    margin-top: 15px;
  }

  @include notebook {
    margin: 15px 0 0;
  }
}

.not-found {
  text-align: center;

  @include notebook {
    margin-top: 10px;
  }
}

.categories {
  background-image: linear-gradient(180deg, var(--secondary-bg) 0%, var(--bg) 100%);
  border-radius: 10px;
  padding: 20px 15px;
  flex: 0 0 300px;

  @include notebook {
    display: none;
  }

  &__heading {
    color: var(--text-tertiary);
    font-weight: 600;
    font-size: 20px;
    margin-bottom: 25px;
  }
}

.category {
  &:not(:last-child) {
    margin-bottom: 5px;
  }

  &__link {
    display: flex;
    justify-content: space-between;
    padding: 3px 0;
  }

  &__count {
    color: var(--text-primary);
    font-size: 12px;
    font-weight: 400;
  }
}
</style>
